import { NO_PROFILES, useProfiles } from "../../queries";
import { useState } from "react";
import {
  Button,
  Dialog,
  Select,
  TextArea,
  TextField,
} from "@jkast90/kast-common/ui";
import { CURRENT_USER_ID } from "../../services/kindlyApi";

const FORM_ID = "thank-you-form";

interface ThankYouData {
  to_user_id: string;
  gift_description: string;
  message: string;
}

interface ThankYouDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ThankYouData) => void;
  preselectedUserId?: string | number;
  preselectedGiftDescription?: string;
}

export default function ThankYouDialog({
  open,
  onClose,
  onSave,
  preselectedUserId,
  preselectedGiftDescription,
}: ThankYouDialogProps) {
  const [formData, setFormData] = useState<ThankYouData>({
    to_user_id: "",
    gift_description: "",
    message: "",
  });
  const [missingTo, setMissingTo] = useState(false);
  const { data: profiles = NO_PROFILES } = useProfiles();
  const wishers = profiles.filter((p) => p.id !== CURRENT_USER_ID);

  // Reset the form whenever the dialog opens or the preselection changes,
  // during render rather than in an effect so it never paints stale values.
  const [prev, setPrev] = useState<{
    open: boolean;
    preselectedUserId?: string | number;
    preselectedGiftDescription?: string;
  } | null>(null);
  if (
    !prev ||
    prev.open !== open ||
    prev.preselectedUserId !== preselectedUserId ||
    prev.preselectedGiftDescription !== preselectedGiftDescription
  ) {
    setPrev({ open, preselectedUserId, preselectedGiftDescription });
    if (open) {
      setMissingTo(false);
      setFormData({
        // The select's value is a string; ids arrive as numbers.
        to_user_id: preselectedUserId ? String(preselectedUserId) : "",
        gift_description: preselectedGiftDescription || "",
        message: "",
      });
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.to_user_id) {
      setMissingTo(true);
      return;
    }
    if (!formData.message.trim()) {
      return;
    }
    onSave(formData);
  };

  const selectedUser = wishers.find(
    (w) => String(w.id) === formData.to_user_id,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Send Thank You Note"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={FORM_ID}>
            Send Thank You
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <Select
          label="To"
          required
          name="to_user_id"
          placeholder="Select family member..."
          options={wishers.map((wisher) => ({
            value: String(wisher.id),
            label: `${wisher.first_name} ${wisher.last_name}`,
          }))}
          value={formData.to_user_id || null}
          onChange={(value) => {
            setMissingTo(false);
            setFormData((prev) => ({ ...prev, to_user_id: value ?? "" }));
          }}
          error={missingTo ? "Choose who to thank" : undefined}
        />
        <TextField
          label="Gift"
          required
          name="gift_description"
          value={formData.gift_description}
          onChange={handleChange}
          placeholder="What gift are you thanking them for?"
        />
        <TextArea
          label="Thank You Message"
          required
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          placeholder={`Dear ${selectedUser ? selectedUser.first_name : "[Name]"}, thank you so much for...`}
        />
      </form>
    </Dialog>
  );
}
